import { useCallback, useRef } from 'react';

import { GeographyTypeEnum } from '@nocowanie/core/enums/geography-type.enum';
import { CommonModalsEnum } from '@nocowanie/common-ui/enums';
import { SearchEngineModel } from '@nocowanie/common-ui/models';

import { clearUnsupportedSearchFiltersAction, setPageAction, useAppDispatch } from '@app/app-state';
import { filterUnsupportedFilters } from '@app/helpers/listing.helpers';
import { resolveLocationData } from '@app/helpers/search.helpers';
import { runWhenIdle } from '@app/helpers/ssr.helpers';
import { useSearch } from '@app/hooks/useSearch';
import { useSsrListingRedirect } from '@app/hooks/useSsrListingRedirect';

import {
    buildFinalSearchData,
    buildInPageUpdatePayload,
    buildLegacyRedirectParams,
    handleAccommodationRedirect,
    isCriteriaUnchanged,
} from './listing-search-bar.helpers';
import { UseSearchEngineSubmitParams } from './listing-search-bar.props';

export function useSearchEngineSubmit({
    lastSelectedLocationRef,
    itemsRef,
    handleItemSelection,
    setCookie,
    getSearchCriteriaFromCookie,
    onClose,
}: UseSearchEngineSubmitParams) {
    const dispatch = useAppDispatch();
    const { tryRedirectToSsrListing } = useSsrListingRedirect();
    const { searchCriteria, filters, updateSearchCriteria } = useSearch();

    const searchCriteriaRef = useRef(searchCriteria);
    searchCriteriaRef.current = searchCriteria;

    return useCallback(
        ({ searchEngineData }: { searchEngineData: SearchEngineModel }) => {
            const newFilters = filterUnsupportedFilters(filters);
            dispatch(clearUnsupportedSearchFiltersAction());

            const resolvedLocation = resolveLocationData({
                lastSelectedLocation: lastSelectedLocationRef.current,
                searchEngineData,
                items: itemsRef.current,
                handleItemSelection,
                fallbackLocationType: searchCriteria.locationType,
            });

            if (resolvedLocation.earlyReturn) return;

            const { locationData, resolvedItem } = resolvedLocation;
            const finalData = buildFinalSearchData(searchEngineData, resolvedLocation);

            // Path 1: Accommodation redirect
            if (resolvedItem?.type === GeographyTypeEnum.accommodation) {
                // Prevent hash manipulation on URL change
                sessionStorage.removeItem(CommonModalsEnum.SearchEngine);

                handleAccommodationRedirect({
                    resolvedItem,
                    finalData,
                    setCookie,
                    handleItemSelection,
                    getSearchCriteriaFromCookie,
                });
                return;
            }

            // Path 2: Navigate to a new location listing page
            if (
                !!lastSelectedLocationRef.current &&
                tryRedirectToSsrListing({
                    selectedItem: resolvedItem,
                    finalData,
                    filters: newFilters,
                    setCookie,
                })
            ) {
                return;
            }

            // Path 3: Update search criteria in-page (no new location selected)
            if (!lastSelectedLocationRef.current) {
                if (
                    isCriteriaUnchanged({
                        finalData,
                        currentCriteria: searchCriteriaRef.current,
                    })
                ) {
                    onClose();
                    return;
                }

                onClose();

                requestAnimationFrame(() => {
                    setCookie(finalData);
                    updateSearchCriteria(buildInPageUpdatePayload(finalData), true);
                    dispatch(setPageAction({ page: 1, shouldUpdateUrl: true }));
                });

                // Safari iOS is being Safari and has issues with simple scroll so it has to de delayed
                runWhenIdle(() => window.scroll({ top: -1, left: 0, behavior: 'smooth' }));

                return;
            }

            // Path 4: Legacy listing redirect (fallback)

            // Prevent hash manipulation on URL change
            sessionStorage.removeItem(CommonModalsEnum.SearchEngine);

            setCookie(finalData);
            window.location.href = buildLegacyRedirectParams({
                finalData,
                locationData,
                resolvedItem,
                searchCriteria,
                filters: newFilters,
            });
        },
        [
            handleItemSelection,
            searchCriteria.locationType,
            searchCriteria.geoId,
            filters,
            setCookie,
            getSearchCriteriaFromCookie,
            updateSearchCriteria,
            onClose,
        ],
    );
}
